import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Clock, CreditCard, Lock, Zap } from 'lucide-react';
type PlanId = 'fuerza-mayor' | 'pack-completo';
interface Plan {
  id: PlanId;
  emoji: string;
  name: string;
  description: string;
  oldPrice: string;
  price: string;
  highlight?: boolean;
  badge?: string;
  savings?: string;
  url: string;
}
const PLANS: Plan[] = [{
  id: 'fuerza-mayor',
  emoji: '📘',
  name: 'FUERZA MAYOR',
  description: '6 tomos + 60+ clases + bonos',
  oldPrice: '$34.990',
  price: '$19.990',
  url: 'https://pagos.recursosfitness.shop/cart/44587881496673:1'
}, {
  id: 'pack-completo',
  emoji: '🏆',
  name: 'PACK COMPLETO',
  description: '6 tomos + Patologías Pro™ + bonos',
  oldPrice: '$43.990',
  price: '$27.990',
  highlight: true,
  badge: '⭐ MÁS ELEGIDO',
  savings: 'AHORRÁS $16.000',
  url: 'https://pagos.recursosfitness.shop/cart/44587920883809:1'
}];
export function PricingCards() {
  const [selected, setSelected] = useState<PlanId>('pack-completo');
  const selectedPlan = PLANS.find(p => p.id === selected) ?? PLANS[1];
  const handleBuy = () => {
    window.location.href = selectedPlan.url;
  };
  return <section id="pricing" className="py-8 md:py-12 px-4 bg-navy-900" data-id="element-203">
      <div className="max-w-xl mx-auto" data-id="element-204">
        <div className="text-center mb-6" data-id="element-205">
          <h2 className="font-display text-3xl md:text-4xl text-white uppercase tracking-wide" data-id="element-206">
            Elegí tu Fuerza Mayor
          </h2>
          <p className="text-sm md:text-base text-navy-200 font-medium mt-1" data-id="element-207">
            Acceso inmediato · Garantía 7 días
          </p>
        </div>

        {/* Options */}
        <div className="flex flex-col gap-3 mb-5" data-id="element-208">
          {PLANS.map(plan => {
          const isSelected = selected === plan.id;
          const isHighlight = plan.highlight;
          return <motion.button key={plan.id} type="button" onClick={() => setSelected(plan.id)} whileTap={{
            scale: 0.99
          }} aria-pressed={isSelected} className={`relative w-full text-left rounded-2xl p-4 md:p-5 transition-all duration-200 border-2 ${isSelected ? isHighlight ? 'bg-navy-800 border-gold shadow-[0_0_30px_rgba(212,165,55,0.35)]' : 'bg-navy-800 border-gold shadow-[0_0_20px_rgba(212,165,55,0.25)]' : 'bg-navy-800/60 border-navy-700 hover:border-navy-500'}`} data-id="element-209">
                <div className="flex items-center gap-3 md:gap-4" data-id="element-210">
                  {/* Radio */}
                  <div className={`shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-gold' : 'border-navy-500'}`} data-id="element-211">
                    <AnimatePresence data-id="element-212">
                      {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} transition={{
                    duration: 0.15
                  }} className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-gold" data-id="element-213" />}
                    </AnimatePresence>
                  </div>

                  {/* Content */}
                  <div className="flex-grow min-w-0" data-id="element-214">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-215">
                      <span className="text-base md:text-lg" data-id="element-216">{plan.emoji}</span>
                      <h3 className="font-bold text-white text-sm md:text-base tracking-wide" data-id="element-217">
                        {plan.name}
                      </h3>
                    </div>
                    {isHighlight && <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-id="element-218">
                        <span className="inline-flex items-center bg-gold text-navy-900 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-219">
                          {plan.badge}
                        </span>
                        <span className="inline-flex items-center bg-navy-700 text-gold text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-220">
                          {plan.savings}
                        </span>
                      </div>}
                    <p className="text-navy-200 text-xs md:text-sm mt-1" data-id="element-221">
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="shrink-0 text-right" data-id="element-222">
                    <div className="text-navy-400 line-through text-xs md:text-sm font-medium leading-tight" data-id="element-223">
                      {plan.oldPrice}
                    </div>
                    <div className={`font-display text-2xl md:text-3xl leading-tight ${isHighlight ? 'text-gold' : 'text-white'}`} data-id="element-224">
                      {plan.price}
                    </div>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Shared CTA */}
        <motion.button onClick={handleBuy} whileHover={{
        scale: 1.02,
        y: -1
      }} whileTap={{
        scale: 0.98
      }} className="w-full bg-gold hover:bg-gold-500 text-navy-900 font-bold text-base md:text-lg py-4 px-6 rounded-xl shadow-[0_0_25px_rgba(212,165,55,0.4)] transition-colors flex items-center justify-center gap-2" data-id="element-225">
          SÍ, QUIERO {selectedPlan.name} →
        </motion.button>

        <p className="text-center text-navy-300 text-xs md:text-sm italic mt-3 mb-4" data-id="element-226">
          {selectedPlan.highlight ? 'La opción más elegida por profes que quieren trabajar con seguridad total' : 'Tu año completo de entrenamiento con adultos mayores resuelto'}
        </p>

        {/* Trust badges - compact single row */}
        <div className="bg-navy-800/60 border border-navy-700 rounded-xl px-4 py-3 flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm font-medium text-navy-100" data-id="element-227">
          <div className="flex items-center gap-1.5" data-id="element-228">
            <Lock size={14} className="text-gold" data-id="element-229" /> Pago seguro
          </div>
          <div className="flex items-center gap-1.5" data-id="element-230">
            <Zap size={14} className="text-gold" data-id="element-231" /> Acceso en 5 min
          </div>
          <div className="flex items-center gap-1.5" data-id="element-232">
            <Shield size={14} className="text-gold" data-id="element-233" /> Garantía 7 días
          </div>
          <div className="flex items-center gap-1.5" data-id="element-234">
            <CreditCard size={14} className="text-gold" data-id="element-235" /> Mercado Pago
          </div>
        </div>
      </div>
    </section>;
}